// RC10p · Pagination helper used by Schedule (and other callers of
// assetApi.list / driverApi.list) to iterate across pages instead of
// requesting a single oversized page_size that the backend silently
// truncates.
//
// Contract:
//   paginatedFetch(fetchFn, { pageSize, maxPages = 50 })
//
// `fetchFn(page, pageSize)` MUST return (either as the resolved
// value or inside a Promise):
//   {
//     items:        [{id, ...}, ...]    // RC10n envelope (preferred)
//     has_next:     boolean,            // authoritative stop signal
//     page:         number,             // echoed by backend
//     total_items?: number,
//     total_pages?: number,
//     // Legacy keys (`results`, `total`) are also accepted — see
//     // _normalize() below.
//   }
//
// Guarantees:
//   * Stops when the backend reports `has_next === false`. No reliance
//     on `items.length < pageSize` heuristics (which are wrong when a
//     page is partially filtered server-side).
//   * Loop guard — tracks visited page numbers and total_pages. If the
//     backend returns the same page number twice OR we exceed
//     total_pages / maxPages, we FAIL FAST with a structured error.
//     The helper never loops indefinitely.
//   * Deduplicates results across pages by stable `id` field. First
//     occurrence wins. (Belt-and-suspenders against overlapping pages
//     from a non-stable sort cursor.)
//   * Error propagation — if any single page request throws, the
//     helper rejects WITHOUT returning a partial list as "complete".
//     Callers that want partial results on error must catch and inspect
//     err.partial on the rejected error object.
//   * Returns a `{items, pagesFetched, totalItems, truncated: false}`
//     shape on success. `truncated` is only ever `true` if the backend
//     itself signals truncation (envelope.truncated === true from the
//     RC10n 'all' path); that case is surfaced but still returns the
//     collected items.
//
// Why not fetch everything with page_size="all"?
//   For assets, the RC10n `all` path is capped at 5000 and still
//   returns a complete-dataset response. For drivers, there is no
//   `all` sentinel and the per-page cap is 200. The uniform iteration
//   pattern below works for every list endpoint on the platform and
//   produces identical semantics for assets, drivers, customers, etc.

const DEFAULT_MAX_PAGES = 50;

function _normalize(envelope, requestedPage) {
  if (!envelope || typeof envelope !== "object") {
    throw new Error("paginatedFetch: fetchFn returned a non-object response");
  }
  const items = Array.isArray(envelope.items)
    ? envelope.items
    : (Array.isArray(envelope.results) ? envelope.results : null);
  if (items === null) {
    throw new Error("paginatedFetch: fetchFn response missing items/results array");
  }
  const totalItems = (typeof envelope.total_items === "number")
    ? envelope.total_items
    : (typeof envelope.total === "number" ? envelope.total : null);
  const totalPages = (typeof envelope.total_pages === "number")
    ? envelope.total_pages
    : null;
  // `has_next` is REQUIRED to make the loop guard tight. If the
  // backend doesn't report it, fall back to total/total_pages math.
  let hasNext;
  if (typeof envelope.has_next === "boolean") {
    hasNext = envelope.has_next;
  } else if (totalItems !== null && typeof envelope.page_size === "number") {
    const effectivePage = typeof envelope.page === "number" ? envelope.page : requestedPage;
    hasNext = (effectivePage * envelope.page_size) < totalItems;
  } else {
    // Unknown — assume no more to avoid runaway loops.
    hasNext = false;
  }
  const page = typeof envelope.page === "number" ? envelope.page : requestedPage;
  const truncated = envelope.truncated === true;
  return { items, totalItems, totalPages, hasNext, page, truncated };
}

export async function paginatedFetch(fetchFn, opts = {}) {
  const pageSize = opts.pageSize;
  const maxPages = opts.maxPages || DEFAULT_MAX_PAGES;
  if (typeof fetchFn !== "function") {
    throw new Error("paginatedFetch: fetchFn must be a function");
  }
  if (!Number.isInteger(pageSize) || pageSize <= 0) {
    throw new Error("paginatedFetch: pageSize must be a positive integer");
  }

  const collected = [];
  const seenPages = new Set();
  const seenIds = new Set();
  let pagesFetched = 0;
  let totalItems = null;
  let truncatedFlag = false;

  let page = 1;
  // Explicit upper bound derived from first response's total_pages
  // once we have it. Prevents malformed `has_next: true` from the
  // backend from spinning forever.
  let authoritativeTotalPages = null;

  // Note: errors inside fetchFn propagate verbatim after we attach a
  // `partial` field with what we've collected so far. Callers that
  // want to recover partially can inspect err.partial.
  while (true) {
    pagesFetched += 1;
    if (pagesFetched > maxPages) {
      const err = new Error(
        `paginatedFetch: exceeded maxPages=${maxPages} (likely runaway pagination)`,
      );
      err.code = "max_pages_exceeded";
      err.partial = collected;
      throw err;
    }
    if (seenPages.has(page)) {
      const err = new Error(
        `paginatedFetch: page ${page} returned twice — backend pagination appears broken`,
      );
      err.code = "duplicate_page";
      err.partial = collected;
      throw err;
    }
    seenPages.add(page);

    let envelope;
    try {
      envelope = await fetchFn(page, pageSize);
    } catch (fetchErr) {
      fetchErr.partial = collected;
      fetchErr.pagesFetched = pagesFetched - 1;
      throw fetchErr;
    }
    const norm = _normalize(envelope, page);
    if (norm.totalItems !== null) totalItems = norm.totalItems;
    if (norm.totalPages !== null && authoritativeTotalPages === null) {
      authoritativeTotalPages = norm.totalPages;
    }
    if (norm.truncated) truncatedFlag = true;

    for (const row of norm.items) {
      const id = row && row.id;
      if (id && seenIds.has(id)) continue;
      if (id) seenIds.add(id);
      collected.push(row);
    }

    if (!norm.hasNext) break;

    // Guard: cannot exceed total_pages reported by backend.
    if (authoritativeTotalPages !== null && page >= authoritativeTotalPages) {
      // Backend disagreement — total_pages says we're done but
      // has_next said true. Stop; log a soft warning via a thrown
      // signal object so callers notice.
      // eslint-disable-next-line no-console
      console.warn(
        `paginatedFetch: has_next=true but page ${page} == total_pages=${authoritativeTotalPages} — stopping`,
      );
      break;
    }

    page += 1;
  }

  return {
    items: collected,
    pagesFetched,
    totalItems: totalItems !== null ? totalItems : collected.length,
    truncated: truncatedFlag,
  };
}

// Convenience adapter: wraps a `*Api.list({page, page_size, ...rest})`
// style function so callers can pass extra query params once and
// paginatedFetch will carry them across pages.
export function wrapListApi(listFn, extraParams = {}) {
  return (page, pageSize) => listFn({ ...extraParams, page, page_size: pageSize });
}

export default paginatedFetch;
